// SW-Registrierungs-Helper. Wird einmal beim App-Boot ausgeführt:
//   1. Alten Trip-spezifischen SW (/trips/<slug>/sw.js aus der Vanilla-Version)
//      aktiv abmelden, damit es keine zwei konkurrierenden SWs gibt.
//   2. Neuen Root-SW (/sw.js) mit Scope `/trips/<slug>/` registrieren.

import { tripBasename } from './env';

const NEW_SW_URL = '/sw.js';

export function isPushSupported(): boolean {
  return typeof navigator !== 'undefined'
    && 'serviceWorker' in navigator
    && typeof window !== 'undefined'
    && 'PushManager' in window;
}

/**
 * iOS-Heuristik: User-Agent enthält iPhone/iPad/iPod (iPadOS 13+ meldet sich
 * unter Safari/Desktop ohne diese Tokens, aber dort haben wir andere Issues —
 * für das Web-Push-UI ist es OK, das Edge-Case nicht abzudecken).
 */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

/**
 * "Im Home-Screen installiert" — für iOS 16.4+ ist Web Push nur in diesem
 * Modus verfügbar. Checks: standalone-Display-Mode (Standard) ODER der
 * iOS-spezifische `navigator.standalone`-Flag.
 */
export function isStandalonePwa(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  return !!(window.navigator as Navigator & { standalone?: boolean }).standalone;
}

/**
 * Registriert den neuen Root-SW mit Scope `/trips/<slug>/`. Vorher werden
 * alle alten SW-Registrierungen unter demselben Scope abgemeldet (das ist
 * der alte vanilla `/trips/<slug>/sw.js`).
 */
export async function registerTripServiceWorker(slug: string): Promise<ServiceWorkerRegistration | null> {
  if (!isPushSupported()) return null;
  const scope = `${tripBasename(slug)}/`;

  // Vorhandene Registrierungen prüfen.
  const existing = await navigator.serviceWorker.getRegistrations().catch(() => []);
  for (const reg of existing) {
    // Alten Trip-SW unter /trips/<slug>/sw.js abmelden (gleicher Scope, anderer SW).
    if (reg.scope.endsWith(scope) && reg.active && reg.active.scriptURL.endsWith(`${scope}sw.js`)) {
      try { await reg.unregister(); } catch (_) {}
    }
  }

  // Neuen Root-SW (oder vorhandene, wenn schon registriert) holen.
  try {
    return await navigator.serviceWorker.register(NEW_SW_URL, { scope });
  } catch (_) {
    return null;
  }
}
